import {
  getCase, checks, paid, pending, advance, addException, audit, uid, today, outstanding,
  syncExceptions, validateReceiptImport, receiptSample, initialState, COLLECTION_TYPES,
  auditCsv, money, partnerExposure
} from "./lib/engine";

const download = (text, name) => {
  const blob = new Blob([text], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

export function useActions({ actor, who, commit, notify, closeModal, navigate, openModal }) {
  const guard = fn => {
    try { fn(); } catch (error) { notify(error.message); }
  };

  const requestAdvance = id => {
    if (actor !== "maker") return notify("Only an Operations maker account can do this.");
    let msg;
    commit(s => {
      const c = getCase(s, id);
      const failures = checks(s, c).filter(item => !item[1]);
      if (failures.length) {
        const type = paid(s, c) || pending(s, c) ? "Duplicate payout attempt"
          : failures.some(item => /exposure/i.test(item[0])) ? "Exposure limit breach" : "Advance eligibility hold";
        const reason = failures.map(item => item[0]).join("; ");
        addException(s, c, type, advance(c), reason);
        audit(s, who, "Advance blocked: " + type, c, advance(c));
        msg = "Blocked: " + reason;
        return;
      }
      s.payments.push({ id: uid("PAY"), caseId: c.id, disb: c.disb, amount: advance(c), status: "Pending", maker: who, date: today() });
      audit(s, who, "Advance requested; exposure reserved", c, advance(c));
      msg = "Requested. A Finance checker must sign in to approve.";
    });
    notify(msg);
  };

  const approveAdvance = (id, reject = false) => guard(() => {
    commit(s => {
      const c = getCase(s, id);
      const instruction = pending(s, c);
      if (!instruction) throw Error("No pending instruction.");
      if (actor !== "checker" || who === instruction.maker) throw Error("Independent Finance checker required.");
      if (reject) {
        instruction.status = "Rejected";
        audit(s, who, "Advance rejected; reservation released", c, instruction.amount);
        return;
      }
      const failures = checks(s, c, true).filter(item => !item[1]);
      if (failures.length) throw Error("Blocked: " + failures.map(item => item[0]).join("; "));
      instruction.status = "Paid";
      instruction.checker = who;
      audit(s, who, "Advance approved and paid — simulation", c, instruction.amount);
    });
    notify(reject ? "Request rejected." : "Simulated payment recorded. No funds moved.");
  });

  const verifyCase = id => {
    if (actor !== "maker") return notify("Only an Operations maker account can do this.");
    commit(s => {
      const c = getCase(s, id);
      c.verified = true;
      audit(s, who, "Disbursement verified — simulation", c);
      syncExceptions(s);
    });
    notify("Disbursement verified (simulation).");
  };

  const createCase = e => {
    e.preventDefault();
    const data = new FormData(e.target);
    guard(() => {
      if (actor !== "maker") throw Error("Only an Operations maker account can do this.");
      const v = Object.fromEntries(data);
      const amount = Number(v.amount);
      const bankRate = Number(v.bankRate);
      const dsaRate = Number(v.dsaRate);
      if (!v.name.trim() || !v.city.trim() || !v.ref.trim()) throw Error("Name, city and reference cannot be blank.");
      if (!Number.isFinite(amount) || amount < 10000 || amount > 100000000) throw Error("Invalid disbursement amount.");
      if (!Number.isFinite(bankRate) || !Number.isFinite(dsaRate) || bankRate <= 0 || bankRate > 10 || dsaRate <= 0 || dsaRate > bankRate) {
        throw Error("Use positive rates; DSA rate cannot exceed bank rate.");
      }
      if (v.due < v.date || v.date > today()) throw Error("Check disbursement and commission due dates.");
      commit(s => {
        if (s.cases.some(c => c.bank === v.bank && c.ref.toLowerCase() === v.ref.trim().toLowerCase())) {
          throw Error("This lender reference already exists.");
        }
        const c = {
          id: uid("LC"), disb: uid("TR"), name: v.name.trim(), partner: v.partner, bank: v.bank,
          city: v.city.trim(), ref: v.ref.trim(), amount, bankRate, dsaRate, advancePct: 80,
          date: v.date, due: v.due, verified: data.has("verified"), agreement: "DEMO-V1"
        };
        s.cases.push(c);
        audit(s, who, "Loan case created", c);
        syncExceptions(s);
      });
      closeModal();
      navigate("Loan Cases");
      notify("Loan created with unique case and disbursement IDs.");
    });
  };

  const importReceipts = text => guard(() => {
    if (actor !== "checker") throw Error("Only a Finance checker account can do this.");
    let count = 0;
    commit(s => {
      const rows = validateReceiptImport(s, text);
      s.receipts.push(...rows);
      rows.forEach(row => audit(s, who, "Receipt imported and matched: " + row.external, getCase(s, row.caseId), row.amount));
      syncExceptions(s);
      count = rows.length;
    });
    closeModal();
    notify(`${count} receipt(s) imported. Reconciliation updated.`);
  });

  const updateException = (e, id) => {
    e.preventDefault();
    const data = new FormData(e.target);
    guard(() => {
      commit(s => {
        const item = s.exceptions.find(x => x.id === id);
        const c = getCase(s, item.caseId);
        const status = data.get("status");
        if (status === "Resolved" && COLLECTION_TYPES.includes(item.type) && outstanding(s, c) > 0) {
          throw Error("Bank commission remains outstanding. Import the receipt first.");
        }
        const owner = String(data.get("owner")).trim();
        const note = String(data.get("note")).trim();
        if (!owner || !note) throw Error("Owner and follow-up note are required.");
        Object.assign(item, { owner, note, due: data.get("due"), status });
        audit(s, who, "Exception updated: " + item.type, c);
      });
      closeModal();
      notify("Exception updated.");
    });
  };

  const downloadTemplate = () => {
    download(receiptSample(), "receipt-template.csv");
    notify("CSV template downloaded.");
  };

  const exportAudit = state => {
    if (!state.audit.length) return notify("No audit entries to export yet.");
    download(auditCsv(state), `loancontrol-audit-${today()}.csv`);
    notify(`${state.audit.length} audit entries exported.`);
  };

  const updatePartnerLimit = (e, id) => {
    e.preventDefault();
    const value = Number(new FormData(e.target).get("limit"));
    guard(() => {
      if (actor !== "checker") throw Error("Only a Finance checker account can do this.");
      if (!Number.isInteger(value) || value < 0 || value > 10000000) {
        throw Error("Enter a whole rupee limit between ₹0 and ₹1,00,00,000.");
      }
      let msg;
      commit(s => {
        const p = s.partners.find(x => x.id === id);
        if (value === p.limit) throw Error("Limit is unchanged.");
        s.limitHistory = s.limitHistory || [];
        s.limitHistory.unshift({ time: new Date().toISOString(), actor, by: who, partnerId: p.id, from: p.limit, to: value });
        audit(s, who, `Partner limit changed: ${p.id} ${p.name} ${money(p.limit)} → ${money(value)}`, null, value);
        p.limit = value;
        const exp = partnerExposure(s, p);
        msg = exp > value
          ? `Limit updated. Current exposure ${money(exp)} exceeds it; new advances will be blocked.`
          : "Partner limit updated and logged in the audit.";
      });
      closeModal();
      notify(msg);
    });
  };

  const resetDemo = () => {
    commit(() => {
      const fresh = initialState();
      syncExceptions(fresh);
      return fresh;
    });
    closeModal();
    navigate("Dashboard");
    notify("Original fictional data restored.");
  };

  return {
    requestAdvance, approveAdvance, verifyCase, createCase, importReceipts,
    updateException, downloadTemplate, exportAudit, updatePartnerLimit, resetDemo, openReset: () => openModal({ type: "reset" })
  };
}
